import React, { useState, useEffect } from 'react';
import {
  Input, Button, message, Pagination, Card, Popconfirm, Tooltip, Divider, Skeleton,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageTitle from '../../Ui/PageTitle';
import { FaQuestionCircle } from "react-icons/fa";
import { LuClipboardList, LuClipboardPenLine } from 'react-icons/lu';
import getErrorMessage from '../../../hooks/getErrorMssg';
import drEasyApi from '../../../Api/Customapi';

const questionsPerPage = 5;

const CreateBookingForm = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [newQuestionText, setNewQuestionText] = useState('');
  const [newQuestionTextAr, setNewQuestionTextAr] = useState('');
  const [formRequired, setFormRequired] = useState(false);

  const { data: formData, isLoading, isError, error } = useQuery({
    queryKey: ['bookingForm'],
    queryFn: async () => {
      try {
        const { data } = await drEasyApi.get('/api/Book/forms/my-form');
        return {
          ...data,
          originalQuestions: data.questions || [],
        };
      } catch (err) {
        if (err.response?.status === 404) {
          return { questions: [], originalQuestions: [] };
        }
        if (err.response.status == 403) {
          window.location.href = "/sub";
        }
        throw err;
      }
    },
    staleTime: Infinity,
    retry: (failureCount, error) => {
      if (error.response?.status === 404) return false;
      return failureCount < 3;
    },
  });

  const formId = formData?.formId;
  const questions = formData?.questions || [];
  const originalQuestions = formData?.originalQuestions || questions;
  const paginatedQuestions = questions.slice(
    (currentPage - 1) * questionsPerPage,
    currentPage * questionsPerPage
  );

  useEffect(() => {
    if (formData?.isRequired !== undefined) {
      setFormRequired(formData.isRequired);
    }
  }, [formData]);

  const createFormMutation = useMutation({
    mutationFn: (questions) => drEasyApi.post('/api/Book/forms', { questions }),
    onSuccess: () => {
      message.success('Form created successfully');
      queryClient.invalidateQueries(['bookingForm']);
    },
    onError: (err) => {
      message.error(getErrorMessage(err));
    },
  });

  const updateFormMutation = useMutation({
    mutationFn: ({ formId, questions }) => drEasyApi.put('/api/Book/forms', { formId, questions }),
    onSuccess: () => {
      message.success('Form updated successfully');
      queryClient.invalidateQueries(['bookingForm']);
    },
    onError: (err) => {
      message.error(getErrorMessage(err));
    },
  });

  const toggleFormRequiredMutation = useMutation({
    mutationFn: async () => {
      const { data } = await drEasyApi.put('/api/Book/forms/ToggleFormRequired', {
        bookingFormId: formId,
      });
      return data;
    },
    onSuccess: (data) => {
      setFormRequired(data);
      message.success(`Form requirement ${data ? 'enabled' : 'disabled'}`);
      queryClient.invalidateQueries(['bookingForm']);
    },
    onError: (err) => {
      message.error(getErrorMessage(err));
    },
  });

  const hasUnsavedChanges = () => {
    if (!formId && questions.length > 0) return true;
    if (questions.length !== originalQuestions.length) return true;
    return questions.some(
      (q, i) =>
        q.questionText !== originalQuestions[i]?.questionText ||
        q.questionTextAr !== originalQuestions[i]?.questionTextAr
    );
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setNewQuestionText(question.questionText);
    setNewQuestionTextAr(question.questionTextAr || '');
  };

  const handleSaveQuestion = () => {
    if (!newQuestionText.trim()) {
      message.warning('Question cannot be empty.');
      return;
    }

    const updatedQuestions = editingQuestion
      ? questions.map(q =>
          q === editingQuestion
            ? { ...q, questionText: newQuestionText, questionTextAr: newQuestionTextAr }
            : q
        )
      : [...questions, { questionText: newQuestionText, questionTextAr: newQuestionTextAr }];

    queryClient.setQueryData(['bookingForm'], (oldData) => ({
      ...oldData,
      questions: updatedQuestions,
    }));

    setEditingQuestion(null);
    setNewQuestionText('');
    setNewQuestionTextAr('');
  };

  const handleDeleteQuestion = (question) => {
    const updatedQuestions = questions.filter(q => q !== question);
    queryClient.setQueryData(['bookingForm'], (oldData) => ({
      ...oldData,
      questions: updatedQuestions,
    }));

    if (updatedQuestions.length === 0) setCurrentPage(1);
    else if ((currentPage - 1) * questionsPerPage >= updatedQuestions.length) {
      setCurrentPage(Math.ceil(updatedQuestions.length / questionsPerPage));
    }
  };

  const handleSubmit = async () => {
    if (questions.some(q => !q.questionText.trim())) {
      message.warning('Please fill all questions.');
      return;
    }

    if (formId) {
      await updateFormMutation.mutateAsync({ formId, questions });
    } else {
      await createFormMutation.mutateAsync(questions);
    }
  };

  const isQuestionModified = (question) => {
    if (!formId) return true;
    return !originalQuestions.some(
      q =>
        q.questionText === question.questionText &&
        q.questionTextAr === question.questionTextAr
    );
  };

  if (isLoading) {
    return (
      <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
        <PageTitle title="Booking Form" subtitle="Loading..." />
        <div className="mx-auto mt-6 ">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="md:col-span-2">
              <Skeleton active paragraph={{ rows: 5 }} />
            </Card>
            <Card>
              <Skeleton active paragraph={{ rows: 2 }} />
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (isError && error?.response?.status !== 404) {
    return (
      <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
        <PageTitle title="Booking Form" />
        <div className="mx-auto mt-6 max-w-4xl">
          <div className="bg-red-50 border-l-4 border-red-500 p-4">
            <p className="text-sm text-red-700">Failed to load form: {error?.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
      <PageTitle
        title="Booking Form"
        actionLabel="Add Question"
        subtitle="Design a custom form to collect patient information during booking"
      />

      <div className="mx-auto mt-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Card
              title={(
                <div className="flex items-center">
                  <LuClipboardList className="mr-2 text-primary text-2xl" />
                  Form Questions
                </div>
              )}
              className="shadow-sm"
            >
              {questions.length > 0 ? (
                <div className="space-y-4">
                  {paginatedQuestions.map((q, index) => {
                    const globalIndex = (currentPage - 1) * questionsPerPage + index;
                    return (
                      <div
                        key={globalIndex}
                        className={`flex items-center justify-between p-4 question-card border-l-2 rounded-2xl border-l-primary ${isQuestionModified(q) ? 'bg-primary/10' : ''}`}
                      >
                        <div className="flex flex-col gap-1">
                          <p className="text-gray-700">
                            <span className="font-medium mr-2">{globalIndex + 1}.</span>
                            {q.questionText}
                          </p>
                          {q.questionTextAr && (
                            <p className="text-gray-500 text-sm" dir="rtl">
                              {q.questionTextAr}
                            </p>
                          )}
                        </div>
                        <div className="flex space-x-2 shrink-0">
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditQuestion(q)}
                            className="text-blue-500"
                          />
                          <Popconfirm
                            title="Are you sure you want to delete this question?"
                            onConfirm={() => handleDeleteQuestion(q)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button type="text" icon={<DeleteOutlined />} className="text-red-500" />
                          </Popconfirm>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <FaQuestionCircle size={48} className="mx-auto mb-4 text-gray-400" />
                  <h3 className="text-lg font-medium text-gray-600">No questions added yet</h3>
                  <p className="text-gray-500 mt-1">Click "Add Question" to start building your form</p>
                </div>
              )}

              {questions.length > questionsPerPage && (
                <div className="mt-4 flex justify-center">
                  <Pagination
                    current={currentPage}
                    pageSize={questionsPerPage}
                    total={questions.length}
                    onChange={setCurrentPage}
                    showSizeChanger={false}
                  />
                </div>
              )}

              <Divider />

              <div className="flex justify-end gap-3 flex-wrap">
                <Button
                  size="large"
                  onClick={() => toggleFormRequiredMutation.mutate()}
                  loading={toggleFormRequiredMutation.isLoading}
                  type={formRequired ? 'default' : 'primary'}
                  danger={formRequired}
                >
                  {formRequired ? 'Disable Form Requirement' : 'Make Form Required'}
                </Button>

                <Tooltip title={hasUnsavedChanges() ? '' : 'No changes to save'}>
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleSubmit}
                    loading={createFormMutation.isLoading || updateFormMutation.isLoading}
                    disabled={!hasUnsavedChanges()}
                    icon={<SaveOutlined />}
                  >
                    Save Changes
                  </Button>
                </Tooltip>
              </div>
            </Card>
          </div>

          <div>
            <Card
              title={(
                <div className="flex items-center">
                  <LuClipboardPenLine className="mr-1 text-primary text-2xl" />
                  {editingQuestion ? "Edit Question" : "Add New Question"}
                </div>
              )}
              className="shadow-sm"
            >
              <div className="flex flex-col gap-3">
                <Input
                  value={newQuestionText}
                  onChange={(e) => setNewQuestionText(e.target.value)}
                  placeholder="Enter question text (English)"
                  autoFocus
                />
                <Input
                  value={newQuestionTextAr}
                  onChange={(e) => setNewQuestionTextAr(e.target.value)}
                  placeholder="أدخل نص السؤال (عربي)"
                  dir="rtl"
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleSaveQuestion}
                  loading={createFormMutation.isLoading || updateFormMutation.isLoading}
                  block
                >
                  {editingQuestion ? "Save" : "Add"}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBookingForm;